// composables/useAppTour.ts
import { useShepherd } from 'vue-shepherd'
import { offset, flip, shift } from '@floating-ui/dom'
import { IDS_LOTTERIES_CLASSIC } from '~/utils/constants/home'
import { trackSkipTour, trackOngoingTour, trackOnboardingCompleted } from '~/tracking/onboardingEvents'
import { useResultStore } from '~/store/result'
import { SHOW_EXTERNAL_LINK } from '~/config/featureFlags'

await import('shepherd.js/dist/css/shepherd.css')
await import('~/assets/css/shepherd-custom.css')

const TOP_Y = 200
const TOP_MARGIN = 120
const BOTTOM_MARGIN = 160
const DOT_GAP = 20

type StepDef = {
	key: string
	title: string
	description: string
	element: string
	element_mobile?: string
	position: string
	position_mobile?: string
}

const alignNativeDotToArrow = (stepEl: HTMLElement) => {
	// si ocultaste el dot nativo, no hacemos nada
	if (stepEl.classList.contains('no-native-dot')) return

	const arrow = stepEl.querySelector('.shepherd-arrow') as HTMLElement | null
	if (!arrow) return

	const pop = stepEl.getBoundingClientRect()
	const a = arrow.getBoundingClientRect()

	// Centro X del arrow relativo al popover
	const centerX = a.left - pop.left + a.width / 2
	stepEl.style.setProperty('--dot-left', `${centerX}px`)

	const placement = stepEl.getAttribute('data-popper-placement') || ''

	// Ajuste Y basado en el tamaño real del arrow (sin números mágicos grandes)
	const y = Math.round(a.height / 2) + DOT_GAP

	// limpia primero
	stepEl.style.removeProperty('--dot-top')
	stepEl.style.removeProperty('--dot-bottom')
	stepEl.style.removeProperty('--dot-right')

	if (placement.startsWith('bottom')) {
		// el arrow “sale” por arriba del popover
		stepEl.style.setProperty('--dot-top', `-${y}px`)
	} else if (placement.startsWith('top')) {
		// el arrow “sale” por abajo del popover
		stepEl.style.setProperty('--dot-bottom', `-${y}px`)
	} else if (placement.startsWith('left')) {
		// opcional: si algún día usas left/right, aquí también se puede alinear
		// stepEl.style.setProperty('--dot-top', `${...}px`)
	} else if (placement.startsWith('right')) {
		// idem
	}
}

const createSteps = (t: (k: string) => string): StepDef[] => [
	{
		key: 'all_results',
		title: t('All Results Area'),
		description: t(`Here's the summary! See the overall performance of your numbers in past draws`),
		element: 'table tbody tr:nth-child(1)',
		element_mobile: '.card-result:first-of-type',
		position: 'top',
	},
	{
		key: 'dream_purchases',
		title: t('Dream Purchases'),
		description: t(`See what you could buy today with the jackpot of your chosen lottery`),
		element: 'table tr:nth-child(1) td:nth-child(6)',
		element_mobile: '.card-result .dream-purchases',
		position: 'top',
	},
	{
		key: 'results_history',
		title: t('Results History'),
		description: t(`See the date and amount won each time by this number combination`),
		element: '[data-tour-root="results-table"] .tour-history-cell',
		element_mobile: '.card-result:first-of-type .view-history',
		position: 'left-start',
		position_mobile: 'top',
	},
	{
		key: 'analysis',
		title: t('Analysis Section'),
		description: t(`Dive into the details! Find 'hot' & 'cold' numbers, combinations, and other analyses here`),
		element: '.container-tabs li:nth-child(2)',
		element_mobile: '.container-tabs li:nth-child(2)',
		position: 'bottom',
	},
	{
		key: 'lotteries_results',
		title: t('Lotteries results'),
		description: t(`Want to see other results? Check past draws from various lotteries and dates here`),
		element: '.container-tabs li:nth-child(3)',
		element_mobile: '.container-tabs li:nth-child(3)',
		position: 'bottom',
	},
]

const removeGreenDot = () => {
	if (import.meta.client) {
		document.querySelectorAll('.tour-green-dot').forEach((el) => el.remove())
	}
}

let cleanupAllResultsDot: null | (() => void) = null

const addGreenDotCenteredOnNumbers = (rowEl: Element) => {
	if (!import.meta.client) return null

	const dot = document.createElement('div')
	dot.className = 'tour-green-dot tour-green-dot--all-results'
	document.body.appendChild(dot)

	const isCircleLike = (el: HTMLElement) => {
		const r = el.getBoundingClientRect()
		// filtra cosas muy grandes o muy chicas
		if (r.width < 18 || r.height < 18) return false
		if (r.width > 80 || r.height > 80) return false

		// “redondez”
		const br = getComputedStyle(el).borderRadius
		const brNum = parseFloat(br || '0')
		const minSide = Math.min(r.width, r.height)
		return br.includes('%') || brNum >= minSide * 0.45
	}

	const findNumbersCell = () => {
		const tds = Array.from(rowEl.querySelectorAll('td')) as HTMLElement[]
		if (!tds.length) return rowEl as HTMLElement

		// elegimos la celda con más “círculos” detectados
		let best: { td: HTMLElement; count: number } | null = null

		for (const td of tds) {
			const candidates = Array.from(td.querySelectorAll('*')) as HTMLElement[]
			const circles = candidates.filter(isCircleLike)
			const count = circles.length

			if (!best || count > best.count) best = { td, count }
		}

		// si no encontró nada, fallback a la 4ta/5ta celda (donde suelen estar los números)
		if (!best || best.count === 0) return tds[3] || tds[4] || tds[0]
		return best.td
	}

	const getCircleRects = () => {
		const cell = findNumbersCell()
		const all = Array.from(cell.querySelectorAll('*')) as HTMLElement[]
		const circles = all.filter(isCircleLike)

		// ordenados de izquierda a derecha
		const rects = circles.map((el) => el.getBoundingClientRect()).sort((a, b) => a.left - b.left)

		return rects
	}

	const update = () => {
		const rects = getCircleRects()

		// si aún no encuentra, no muevas (evita saltos)
		if (rects.length < 2) return

		const first = rects[0]
		const last = rects[rects.length - 1]

		// centro horizontal del grupo
		const x = (first.left + last.right) / 2

		// ✅ arriba de las bolitas (pegadito)
		const y = first.top - 20

		dot.style.left = `${x}px`
		dot.style.top = `${y}px`
		document.documentElement.style.setProperty('--tour-dot-x', `${x}px`)
		document.documentElement.style.setProperty('--tour-dot-y', `${y}px`)
	}

	// recalcular varias veces por layout
	update()
	requestAnimationFrame(update)
	setTimeout(update, 120)
	setTimeout(update, 300)

	window.addEventListener('scroll', update, true)
	window.addEventListener('resize', update)

	return () => {
		window.removeEventListener('scroll', update, true)
		window.removeEventListener('resize', update)
		dot.remove()
	}
}

const getArrowButton = (index: number) => {
	if (index === 0) return ''
	const arrow = `<svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M5 12.4854H19" stroke="#323232" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M10 7.48535L5 12.4854" stroke="#323232" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M10 17.4854L5 12.4854" stroke="#323232" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`
	return `<span id="tour-arrow-back-${index}" class="cursor-pointer flex items-center mr-2">${arrow}</span>`
}

const smartScrollTo = (target: Element, forceToTarget = false, preferTop = false) => {
	if (!import.meta.client) return

	const rect = target.getBoundingClientRect()
	const vh = window.innerHeight
	const isMobile = window.matchMedia('(max-width: 768px)').matches

	if (forceToTarget) {
		scrollTarget(target, isMobile, preferTop)
		return
	}

	const isVisible = rect.top >= TOP_MARGIN && rect.bottom <= vh - BOTTOM_MARGIN

	if (isVisible) {
		if (!isMobile) {
			window.scrollTo({ top: TOP_Y, behavior: 'smooth' })
			return
		}
		scrollTarget(target, true, preferTop)
		return
	}

	scrollTarget(target, isMobile, preferTop)
}

const ensureSpaceAboveTargetForTooltip = async (target: Element, padding = 16) => {
	if (!import.meta.client) return

	// El tooltip ya existe cuando se llama desde beforeShowPromise (después de nextTick)
	const tip = document.querySelector('.shepherd-element') as HTMLElement | null
	if (!tip) return

	const tipH = tip.getBoundingClientRect().height
	const neededTopSpace = tipH + padding

	const targetTop = target.getBoundingClientRect().top

	// si el target está demasiado cerca del top, baja el viewport (scroll negativo)
	const delta = neededTopSpace - targetTop
	if (delta > 0) {
		window.scrollBy({ top: -delta, behavior: 'smooth' })
	}
}

const scrollTarget = (target: Element, isMobile: boolean, preferTop: boolean) => {
	const el = target as HTMLElement

	el.scrollIntoView({
		behavior: 'smooth',
		block: 'center',
		inline: 'nearest',
	})

	if (isMobile && preferTop) {
		// ✅ deja espacio arriba para que el tooltip "top" quepa
		setTimeout(() => {
			ensureSpaceAboveTargetForTooltip(el, 20)
		}, 250)
	}
}

const scrollToAnchorWithTopSpace = (el: Element, topSpace = 140) => {
	const y = window.scrollY + el.getBoundingClientRect().top - topSpace
	window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' })
}

const hardCleanupShepherdDom = () => {
	if (!import.meta.client) return

	// popovers y contenedores
	document.querySelectorAll('.shepherd-element, .shepherd-modal-overlay-container').forEach((n) => n.remove())

	// clases/attrs que a veces quedan pegados
	document.body.classList.remove('shepherd-active')
	document.body.removeAttribute('aria-hidden')
}

const applyFooterLayout = (stepEl: HTMLElement) => {
	const footer = stepEl.querySelector('.shepherd-footer') as HTMLElement | null
	if (!footer) return

	stepEl.classList.remove('is-footer-stacked')

	requestAnimationFrame(() => {
		// si el footer ocupa más de una línea, apilar
		const isWrapped = footer.scrollHeight > footer.clientHeight + 2
		if (isWrapped) stepEl.classList.add('is-footer-stacked')
	})
}

let rebuilding = false

/**
 * Composable that manages the Shepherd.js onboarding tour for the results page.
 * Handles step creation, smart scrolling, green dot indicators, responsive layouts,
 * and locale-aware rebuilding.
 *
 * Returns `{ tour, initTour }`:
 * - `tour` — the Shepherd tour instance (use to start/stop programmatically).
 * - `initTour()` — idempotent initialiser; builds steps and binds event listeners.
 */
export const useAppTour = () => {
	const route = useRoute()
	const resultStore = useResultStore()
	const { t, locale } = useI18n()
	const { $hasFeature } = useNuxtApp()

	let suppressCancelTracking = false

	const isMultipleBets = computed<boolean>(() => {
		const v = route.query.is_multiple_bets

		if (Array.isArray(v)) {
			return v[0] === 'true'
		}

		return v === 'true'
	})

	const tour = useShepherd({
		useModalOverlay: true,
		defaultStepOptions: {
			scrollTo: false,
			cancelIcon: { enabled: true },
			classes: 'custom-shepherd-theme',
			modalOverlayOpeningPadding: 8,
			modalOverlayOpeningRadius: 12,
		},
	})

	const isMobile = (isClassicLottery: boolean) => {
		const isShowExternal = $hasFeature(SHOW_EXTERNAL_LINK) ? 1400 : 1200
		const size = isClassicLottery ? 1366 : isShowExternal
		return import.meta.client && window.matchMedia(`(max-width: ${size}px)`).matches
	}

	const getClasses = (isClassicLottery: boolean) => {
		if (isClassicLottery === true) return '!max-w-[300px] 2xl:!max-w-[593px] 2xl:!w-full'
		const isShowExternal = $hasFeature(SHOW_EXTERNAL_LINK)
			? '!max-w-[300px] 4xl:!max-w-[593px] 4xl:!w-full'
			: '!max-w-[300px] 3xl:!max-w-[593px] 3xl:!w-full'
		return isShowExternal
	}

	const nextStep = (index: number) => {
		const game_name = resultStore.getLotterySelect?.name || ''
		trackOngoingTour({ game_name, step_id: index + 1 })
	}

	const onCancel = (index: number) => {
		const game_name = resultStore.getLotterySelect?.name || ''
		trackSkipTour({ game_name, step_id: index + 1 })
		removeGreenDot()
	}

	const waitForEl = (selector: string, timeoutMs = 2000) => {
		if (!import.meta.client) return Promise.resolve(false)

		return new Promise<boolean>((resolve) => {
			const start = performance.now()

			const tick = () => {
				if (document.querySelector(selector)) return resolve(true)
				if (performance.now() - start > timeoutMs) return resolve(false)
				requestAnimationFrame(tick)
			}

			tick()
		})
	}

	const addStepsToTour = (defs: StepDef[], mobile: boolean, isClassicLottery: boolean) => {
		defs.forEach((step, index) => {
			const targetSelector = mobile && step.element_mobile ? step.element_mobile : step.element
			const positionSelector = mobile && step.position_mobile ? step.position_mobile : step.position

			let buttons = [
				{
					text: t('Skip tips'),
					action: tour.cancel,
					classes: '!text-sm !text-neutral-800 !leading-5 !py-2 !px-3 !rounded-full !bg-transparent',
				},
				{
					text: index === defs.length - 1 ? t('Finish') : t('Keep exploring'),
					action: () => {
						nextStep(index)
						tour.next()
					},
					classes:
						'!text-base !text-white !bg-primary-400 !rounded-full !font-semibold !leading-6 !py-2 !px-3 hover:!bg-opacity-80 !min-w-36',
				},
			]
			if (index === defs.length - 1) {
				buttons = buttons.filter((b) => b.text !== t('Skip tips'))
			}

			const isHistory = step.key === 'results_history'

			const isStep3or4 = step.key === 'results_history' || step.key === 'analysis'
			const forceTopOnMobile = mobile && isStep3or4

			const attachOn = forceTopOnMobile ? 'top' : positionSelector

			const middleware = [
				offset({ mainAxis: 20, crossAxis: 0 }),
				// ✅ si NO quieres que cambie de lado en mobile
				...(forceTopOnMobile ? [] : [flip()]),
				// opcional: evitar que se salga del viewport
				shift({ padding: 8 }),
			]

			let detachArrowVarsListener: null | (() => void) = null

			tour.addStep({
				id: step.key,
				attachTo: { element: targetSelector, on: attachOn },
				highlightClass: isHistory ? 'shepherd-highlight-cell' : undefined,

				title: () => `<div class="flex gap-1">${getArrowButton(index)}
      <p class="text-[#212529] text-sm">${index + 1}/${defs.length}</p>
    </div>`,

				text: () =>
					`<p class="text-[#212529] font-bold pb-0.5">${step.title}</p>
     <p class="text-black">${step.description}.</p>`,

				classes: getClasses(isClassicLottery),

				buttons,

				floatingUIOptions: { middleware },
				beforeShowPromise: async () => {
					await nextTick()
					await nextTick()

					const found = await waitForEl(targetSelector, 2500)
					if (!found) {
						;(tour as any).cancel()
						return
					}

					let el: Element | null = document.querySelector(targetSelector)
					if (!el) {
						;(tour as any).cancel()
						return
					}

					if (step.key === 'results_history' && !mobile) {
						const wrapper = highlightResultsHistory()
						if (wrapper) el = wrapper
					}

					const isHistory = step.key === 'results_history'
					const preferTop = forceTopOnMobile || attachOn.startsWith('top')

					if (mobile && isHistory) {
						scrollToAnchorWithTopSpace(el, 180)
					} else {
						smartScrollTo(el, isHistory, preferTop)
					}

					if (step.key !== 'all_results') {
						cleanupAllResultsDot?.()
						cleanupAllResultsDot = null
						removeGreenDot()
					}

					setTimeout(() => {
						;(tour as any).getCurrentStep?.()?.updatePosition?.()

						if (mobile && preferTop) {
							ensureSpaceAboveTargetForTooltip(el, 20)
							setTimeout(() => (tour as any).getCurrentStep?.()?.updatePosition?.(), 200)
						}
					}, 350)
				},

				when: {
					show: () => {
						const backArrow = document.getElementById(`tour-arrow-back-${index}`)
						if (backArrow) backArrow.onclick = () => tour.back()

						const stepEl = (tour as any).getCurrentStep?.()?.getElement?.() as HTMLElement | null
						if (!stepEl) return

						const scheduleAlignDot = () => {
							requestAnimationFrame(() => alignNativeDotToArrow(stepEl))
							setTimeout(() => alignNativeDotToArrow(stepEl), 0)
							setTimeout(() => alignNativeDotToArrow(stepEl), 80)
						}

						const updateArrowToDot = () => {
							// solo si existe el dot
							const dotX = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--tour-dot-x') || '')
							if (!Number.isFinite(dotX)) return

							const stepRect = stepEl.getBoundingClientRect()
							const stepCenterX = stepRect.left + stepRect.width / 2

							// cuánto hay que mover la flecha (positivo => derecha, negativo => izquierda)
							let shift = dotX - stepCenterX

							// ✅ clamp para no sacar la flecha del tooltip (margen de seguridad)
							const max = stepRect.width / 2 - 40
							shift = Math.max(-max, Math.min(max, shift))

							stepEl.style.setProperty('--tour-arrow-shift', `${shift}px`)

							// reposiciona popper por si cambió el scroll
							;(tour as any).getCurrentStep?.()?.updatePosition?.()
						}

						// defaults
						stepEl.style.removeProperty('--dot-left')
						stepEl.style.removeProperty('--dot-bottom')
						stepEl.style.removeProperty('--dot-top')
						stepEl.style.removeProperty('--dot-right')

						// ✅ leer el valor ACTUAL (reactivo)
						const multiple = isMultipleBets.value
						stepEl.classList.remove('no-native-dot')
						if (step.key === 'all_results' && !multiple && !mobile) {
							stepEl.classList.add('no-native-dot')
							// ✅ mata cualquier dot anterior
							cleanupAllResultsDot?.()
							cleanupAllResultsDot = null
							removeGreenDot()

							// ✅ coloca dot centrado entre los números del row real
							const row = document.querySelector(targetSelector)
							if (row) {
								cleanupAllResultsDot = addGreenDotCenteredOnNumbers(row)
							}
						}

						if (step.key === 'all_results' && !mobile) {
							detachArrowVarsListener?.()
							detachArrowVarsListener = null

							// primera actualización luego de que el dot ya calculó --tour-dot-x
							setTimeout(updateArrowToDot, 0)
							requestAnimationFrame(updateArrowToDot)
							setTimeout(updateArrowToDot, 120)

							window.addEventListener('scroll', updateArrowToDot, true)
							window.addEventListener('resize', updateArrowToDot)
							window.addEventListener('tour-dot-moved', updateArrowToDot as any)

							detachArrowVarsListener = () => {
								window.removeEventListener('scroll', updateArrowToDot, true)
								window.removeEventListener('resize', updateArrowToDot)
								stepEl.style.removeProperty('--tour-arrow-shift')
								window.removeEventListener('tour-dot-moved', updateArrowToDot as any)
							}
						}

						scheduleAlignDot()

						applyFooterLayout(stepEl)
					},
				},

				hide: () => {
					clearHighlights()
					removeGreenDot()

					cleanupAllResultsDot?.()
					cleanupAllResultsDot = null

					// ✅ limpia listener de arrow vars
					detachArrowVarsListener?.()
					detachArrowVarsListener = null

					const current = (tour as any).getCurrentStep?.()
					const stepEl = current?.getElement?.() as HTMLElement | null
					stepEl?.classList.remove('no-native-dot')
				},
			})
		})
	}

	const initTour = async () => {
		if (!import.meta.client) return
		if ((tour as any).isActive?.()) return
		if (tour.steps?.length) return
		const defs = buildDefs()
		rebuildTourSteps(defs)
	}

	const buildDefs = () => {
		const steps = createSteps(t)
		return $hasFeature('SHOW_DREAM_PURCHASES') ? steps : steps.filter((s) => s.key !== 'dream_purchases')
	}

	const highlightResultsHistory = () => {
		if (!import.meta.client) return null

		document
			.querySelectorAll('.shepherd-highlight-cell')
			.forEach((el) => el.classList.remove('shepherd-highlight-cell'))

		const link = document.querySelector('[data-tour-root="results-table"] [data-tour="view-history"]')
		const wrapper = link?.closest('.tour-history-cell') as HTMLElement | null
		if (!wrapper) return null

		wrapper.classList.add('shepherd-highlight-cell')
		return wrapper
	}

	const clearHighlights = () => {
		if (!import.meta.client) return
		document
			.querySelectorAll('.shepherd-highlight-cell')
			.forEach((el) => el.classList.remove('shepherd-highlight-cell'))
	}

	const bindTourListeners = (defsLength: number) => {
		tour.off('cancel')
		tour.off('complete')

		tour.on('cancel', (event: any) => {
			if (suppressCancelTracking) return
			onCancel(event?.index ?? 0)
		})

		tour.on('complete', () => {
			const game_name = resultStore.getLotterySelect?.name || ''
			trackOnboardingCompleted({ game_name, step_id: defsLength })
			removeGreenDot()
		})
	}

	const rebuildTourSteps = (defs: StepDef[]) => {
		const isClassicLottery = IDS_LOTTERIES_CLASSIC.includes(Number(route.query.lottery_id))
		const mobile = isMobile(isClassicLottery)

		clearTourSteps()
		addStepsToTour(defs, mobile, isClassicLottery)
		bindTourListeners(defs.length)
	}

	const clearTourSteps = () => {
		const t = tour as any

		// 1) Si está activo, cancélalo para que Shepherd deje de mantener DOM/estado del step actual
		if (typeof t.isActive === 'function' && t.isActive()) {
			suppressCancelTracking = true
			try {
				t.cancel()
			} finally {
				suppressCancelTracking = false
			}
		}

		// 2) Elimina por id (lo más fiable en todas las versiones)
		const steps = Array.isArray(t.steps) ? [...t.steps] : []
		if (typeof t.removeStep === 'function') {
			for (const s of steps) {
				if (s?.id) t.removeStep(s.id)
			}
		}

		// 3) Por si existe y funciona, también lo llamamos (no hace daño)
		if (typeof t.removeSteps === 'function') t.removeSteps()

		// 4) Limpia leftovers visuales por si acaso
		removeGreenDot()
		hardCleanupShepherdDom()
	}

	const applyDotVarsToCurrent = () => {
		const current = (tour as any).getCurrentStep?.()
		const stepEl = current?.getElement?.() as HTMLElement | null
		if (!current || !stepEl) return

		stepEl.style.removeProperty('--dot-left')
		stepEl.style.removeProperty('--dot-bottom')
		stepEl.style.removeProperty('--dot-top')
		stepEl.style.removeProperty('--dot-right')

		const multiple = isMultipleBets.value
		const isClassicLottery = IDS_LOTTERIES_CLASSIC.includes(Number(route.query.lottery_id))
		const mobile = isMobile(isClassicLottery)

		if (current.id === 'all_results' && !multiple && !mobile) {
			stepEl.style.setProperty('--dot-left', '70%')
			stepEl.style.setProperty('--dot-bottom', '-20px')
		}
	}
	// Si cambia el idioma, recrea los pasos (el host componente puede volver a llamar startTour o relanzar)
	watch(
		() => locale.value,
		async () => {
			if (!import.meta.client) return
			if (rebuilding) return
			rebuilding = true

			// limpia TODO
			clearTourSteps()

			tour.off('cancel')
			tour.off('complete')

			await nextTick()
			await nextTick()

			requestAnimationFrame(() => {
				const defs = buildDefs()
				rebuildTourSteps(defs)
				rebuilding = false
			})
		},
		{ flush: 'post' },
	)

	watch(
		() => route.query.is_multiple_bets,
		() => {
			if (!(tour as any).isActive?.()) return
			applyDotVarsToCurrent()
		},
		{ flush: 'post' },
	)

	return {
		tour,
		initTour,
	}
}
